// Pure free-tier entitlement logic for the masterclass A/B experiment's Version B.
// No Nuxt/Vue/prisma deps so it's unit-testable and importable from BOTH the client
// (is-verified middleware, page event gating) and the server (API gates).

export const FREE_TIER_LIMITS = { chat: 20, marketing: 4 } as const;
export type FreeFeature = keyof typeof FREE_TIER_LIMITS;

export type FreeTierUser = {
  role: string | null | undefined;
  // Accepts the raw Prisma `string | null` (server authUser) as well as the
  // client's narrowed 'A' | 'B' | null — the runtime check is `=== 'B'`.
  variant: string | null | undefined;
  masterclassPaidAt: string | Date | null | undefined;
};

// True only for an unpaid Version-B lead — the free-tier audience.
export const isFreeTierLead = (u: FreeTierUser): boolean =>
  u.role === 'LEAD' && u.variant === 'B' && !u.masterclassPaidAt;

// Pure access decision given the user's all-time usage count for the feature.
export const resolveFreeAccess = (
  u: FreeTierUser,
  feature: FreeFeature,
  used: number,
): 'allow' | 'limit-reached' | 'not-free-tier' => {
  if (!isFreeTierLead(u)) return 'not-free-tier';
  return used >= FREE_TIER_LIMITS[feature] ? 'limit-reached' : 'allow';
};

// The free Phase-0 lesson counts as "watched" once any Phase-0 step (id "0.x")
// is in the lead's completed-steps list (from User.masterclassProgress).
export const hasWatchedFreeLesson = (completedSteps: string[] | null | undefined): boolean =>
  (completedSteps ?? []).some((s) => s.startsWith('0.'));
